<template>
	<BillingCardForm
		class="card-form"
		:config="config"
		:styles="inputStyles"
		:i18n="i18n"
		:show-cardholder-name="false"
		:postal-code-config="postalCodeConfig"
	>
		<template #button="{ submit, isValidating }">
			<!-- Funnel-identical placement (med-web-funnel CardForm.vue): the submit lives
			     inside the card section, right under the hosted fields. -->
			<div class="card-form__footer">
				<Button class="card-form__submit" full-width :loading="isValidating" @ds:click="submit">
					<Text font="--text-16-18-semi">Confirm</Text>
				</Button>
			</div>
		</template>
	</BillingCardForm>
</template>

<script setup lang="ts">
import { Button, Text } from '@simple-life-app/growth-ui-kova'
import { CardForm as BillingCardForm } from '@simple-life-app/web-billing'
import type { CardInputStyles, FormI18n, IPaymentMethodConfig } from '@simple-life-app/web-billing'

defineProps<{ config: IPaymentMethodConfig }>()

// Hosted-field text styles (applied inside the PCI iframes, so tokens can't be used) —
// the exact literals med-web-funnel passes, for an identical Stripe-iframe rendering.
const inputStyles: CardInputStyles = {
	color: '#323653',
	placeholderColor: '#C5C4C7',
	showLabel: false,
}

// med-web-funnel's full i18n map: labels are hidden (showLabel false), but the error
// strings still surface under the fields.
const i18n: FormI18n = {
	cardholderName: 'Name on card',
	cardholderNamePlaceholder: 'Name on card',
	postalCode: 'Zip code',
	postalCodePlaceholder: 'Zip code',
	cardNumber: 'Card number',
	expirationDate: 'Expires on',
	expirationDatePlaceholder: 'MM/YY',
	taxIdPlaceholder: 'CPF/CNPJ',
	cvv: 'Security code',
	taxId: 'CPF/CNPJ',
	errors: {
		cardholderNameInvalid: 'Name on card is invalid',
		postalCodeInvalid: 'Use the format XXXXX or XXXXX-XXXX for ZIP code',
		cardTaxIdInvalid: 'CPF/CNPJ is invalid',
	},
}

const postalCodeConfig = {
	show: false,
	validator: (v: string) => /^\d{5}(-\d{4})?$/.test(v.trim()),
}
</script>

<style scoped>
/* No --input-* overrides on purpose: med-web-funnel keeps web-billing's default field
   chrome (1px #3236534d border, 8px radius, 50px height) and we match it exactly. */
.card-form {
	width: 100%;
}

.card-form__footer {
	width: 100%;
}

/* Funnel parity: the checkout submit is a 16px-radius rectangle, not the DS pill. */
.card-form__footer .card-form__submit {
	border-radius: var(--size-16);
}
</style>
